import { FileX02Icon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'

// ============================================================================
// Featured Icon (Untitled UI Pattern for Empty States)
// ============================================================================

export type EmptyStateFeaturedIconVariant = 'brand' | 'gray' | 'error' | 'warning' | 'success'
export type EmptyStateFeaturedIconSize = 'sm' | 'md' | 'lg' | 'xl'

const featuredIconVariants: Record<
  EmptyStateFeaturedIconVariant,
  { container: string; icon: string }
> = {
  brand: {
    container: 'bg-brand-100 dark:bg-brand-900/40',
    icon: 'text-brand-600 dark:text-brand-400',
  },
  gray: {
    container: 'bg-gray-100 dark:bg-gray-800',
    icon: 'text-gray-500 dark:text-gray-400',
  },
  error: {
    container: 'bg-error-100 dark:bg-error-900/40',
    icon: 'text-error-600 dark:text-error-400',
  },
  warning: {
    container: 'bg-warning-100 dark:bg-warning-900/40',
    icon: 'text-warning-600 dark:text-warning-400',
  },
  success: {
    container: 'bg-success-100 dark:bg-success-900/40',
    icon: 'text-success-600 dark:text-success-400',
  },
}

const featuredIconSizes: Record<
  EmptyStateFeaturedIconSize,
  { container: string; icon: string }
> = {
  sm: { container: 'h-10 w-10', icon: 'h-5 w-5' },
  md: { container: 'h-12 w-12', icon: 'h-6 w-6' },
  lg: { container: 'h-14 w-14', icon: 'h-7 w-7' },
  xl: { container: 'h-16 w-16', icon: 'h-8 w-8' },
}

// ============================================================================
// Empty State Component
// ============================================================================

export type EmptyStateVariant = 'simple' | 'card' | 'bordered'
export type EmptyStateSize = 'sm' | 'md' | 'lg'

export interface EmptyStateProps {
  /** Main title text */
  title: string
  /** Optional description text */
  description?: string
  /** Custom icon element (defaults to FileX02Icon) */
  icon?: React.ReactNode
  /** Icon variant for featured icon styling */
  iconVariant?: EmptyStateFeaturedIconVariant
  /** Icon size */
  iconSize?: EmptyStateFeaturedIconSize
  /** Hide the featured icon */
  hideIcon?: boolean
  /** Action configuration - either a button config object or custom ReactNode */
  action?:
    | {
        label: string
        onClick: () => void
        variant?: 'primary' | 'secondary' | 'tertiary'
        icon?: React.ReactNode
      }
    | React.ReactNode
  /** Secondary action for two-button layouts */
  secondaryAction?:
    | {
        label: string
        onClick: () => void
      }
    | React.ReactNode
  /** Visual variant */
  variant?: EmptyStateVariant
  /** Size affects padding and typography */
  size?: EmptyStateSize
  /** Additional CSS classes */
  className?: string
}

const sizeClasses: Record<EmptyStateSize, { padding: string; gap: string; title: string }> = {
  sm: {
    padding: 'py-8 px-4',
    gap: 'gap-3',
    title: 'text-base',
  },
  md: {
    padding: 'py-12 px-6',
    gap: 'gap-4',
    title: 'text-lg',
  },
  lg: {
    padding: 'py-16 px-8',
    gap: 'gap-5',
    title: 'text-xl',
  },
}

/**
 * EmptyState - Untitled UI inspired empty state component
 *
 * Displays a placeholder for empty lists, search results, or data tables.
 * Features a prominent featured icon, title, description, and optional actions.
 *
 * Variants:
 * - simple: Basic centered layout without background
 * - card: Subtle gray background with rounded corners
 * - bordered: Dashed border with subtle background (drag-and-drop style)
 */
export function EmptyState({
  title,
  description,
  icon,
  iconVariant = 'gray',
  iconSize = 'lg',
  hideIcon = false,
  action,
  secondaryAction,
  variant = 'simple',
  size = 'md',
  className,
}: EmptyStateProps) {
  const sizes = sizeClasses[size]
  const iconStyles = featuredIconVariants[iconVariant]
  const iconSizeStyles = featuredIconSizes[iconSize]

  // Default icon
  const defaultIcon = <FileX02Icon className="h-full w-full" />

  // Render the featured icon
  const renderIcon = () => {
    if (hideIcon) return null

    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-full',
          iconStyles.container,
          iconSizeStyles.container
        )}
      >
        <div className={cn(iconStyles.icon, iconSizeStyles.icon, '[&>svg]:h-full [&>svg]:w-full')}>
          {icon || defaultIcon}
        </div>
      </div>
    )
  }

  // Render action: either as a React node or as a button from object config
  const renderAction = (
    actionConfig: EmptyStateProps['action'],
    isSecondary: boolean = false
  ) => {
    if (!actionConfig) return null

    // Check if action is a config object
    if (
      typeof actionConfig === 'object' &&
      actionConfig !== null &&
      'label' in actionConfig &&
      'onClick' in actionConfig
    ) {
      const buttonVariant = isSecondary
        ? 'secondary'
        : actionConfig.variant || 'primary'

      return (
        <Button color={buttonVariant} onPress={actionConfig.onClick}>
          {actionConfig.icon}
          {actionConfig.label}
        </Button>
      )
    }

    // Action is a ReactNode
    return actionConfig
  }

  // Content layout
  const content = (
    <>
      {renderIcon()}

      <div className="flex flex-col items-center gap-1 text-center">
        <h3
          className={cn(
            'font-semibold text-gray-900 dark:text-gray-100',
            sizes.title
          )}
        >
          {title}
        </h3>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {description}
          </p>
        )}
      </div>

      {(action || secondaryAction) && (
        <div className="flex items-center justify-center gap-3 mt-2">
          {renderAction(secondaryAction, true)}
          {renderAction(action)}
        </div>
      )}
    </>
  )

  // Variant-specific container styles
  const variantStyles: Record<EmptyStateVariant, string> = {
    simple: '',
    card: 'rounded-xl bg-gray-50 dark:bg-gray-900/50',
    bordered: cn(
      'rounded-xl',
      'border-2 border-dashed border-gray-300 dark:border-gray-700',
      'bg-gray-50/50 dark:bg-gray-900/30'
    ),
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center',
        sizes.padding,
        sizes.gap,
        variantStyles[variant],
        className
      )}
    >
      {content}
    </div>
  )
}

// ============================================================================
// Convenience Components
// ============================================================================

export interface EmptyStateIconProps {
  children: React.ReactNode
  variant?: EmptyStateFeaturedIconVariant
  size?: EmptyStateFeaturedIconSize
  className?: string
}

/**
 * Standalone featured icon component for custom empty state layouts
 */
export function EmptyStateIcon({
  children,
  variant = 'gray',
  size = 'lg',
  className,
}: EmptyStateIconProps) {
  const styles = featuredIconVariants[variant]
  const sizeStyles = featuredIconSizes[size]

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full',
        styles.container,
        sizeStyles.container,
        className
      )}
    >
      <div className={cn(styles.icon, sizeStyles.icon, '[&>svg]:h-full [&>svg]:w-full')}>
        {children}
      </div>
    </div>
  )
}

export default EmptyState
